import React, { useState, useRef, useEffect, useMemo } from 'react';
import Button from './Button';
import { Badge } from './Badge';
import BatchRemarkProgressModal from './BatchRemarkProgressModal';
import RemarkModelsSelectionModal from './RemarkModelsSelectionModal';
import { AssessmentCard } from './AssessmentCard';
import { EssayCard as ShadcnEssayCard } from './EssayCard';
import { AssessmentHeader } from './AssessmentHeader';
import SaveRubricTemplateModal from './SaveRubricTemplateModal';
import { Essay, Assessment, AppStep, ModelResult, RubricTemplateCriterion } from '../src/types';
import { useAssessmentStore } from '../src/store/assessmentStore';
import { DEFAULT_MODEL_ID } from '../constants';
import { cn } from '../lib/utils';
import { Plus, Archive, Filter, Search, SortAsc, SortDesc, CheckSquare, Square, X } from 'lucide-react';

// Extended Essay interface for ranking calculations
interface EssayWithRanks extends Essay {
  aiRank?: number;
  manualRank?: number;
}

// Basic filtering for valid essays - updated for multi-model support
const getValidEssays = (essays: Essay[], modelId?: string) => {
  return essays.filter(essay => {
    // Check manual score
    if (essay.manualOverallScore === null || 
        essay.manualOverallScore === undefined ||
        typeof essay.manualOverallScore !== 'number') {
      return false;
    }
    
    // If modelId specified, check that model's score
    if (modelId) {
      const modelResult = essay.modelResults?.[modelId];
      return modelResult && 
             modelResult.finalOverallScore !== null && 
             modelResult.finalOverallScore !== undefined &&
             typeof modelResult.finalOverallScore === 'number';
    }
    
    // Otherwise check legacy fields for backward compatibility
    return essay.finalOverallScore !== null && 
           essay.finalOverallScore !== undefined &&
           typeof essay.finalOverallScore === 'number';
  });
};

// Ranking calculation - now supports model-specific rankings
const calculateRankings = (essays: Essay[], scoreKey: 'finalOverallScore' | 'manualOverallScore', modelId?: string): EssayWithRanks[] => {
  let validEssays: Essay[];
  
  if (scoreKey === 'finalOverallScore' && modelId) {
    // Model-specific AI ranking
    validEssays = essays.filter(essay => {
      const modelResult = essay.modelResults?.[modelId];
      return modelResult && 
             modelResult.finalOverallScore !== null && 
             modelResult.finalOverallScore !== undefined && 
             typeof modelResult.finalOverallScore === 'number';
    }).sort((a, b) => {
      const scoreA = a.modelResults?.[modelId]?.finalOverallScore || 0;
      const scoreB = b.modelResults?.[modelId]?.finalOverallScore || 0;
      return scoreB - scoreA;
    });
  } else {
    // Legacy or manual ranking
    validEssays = essays.filter(essay => 
      essay[scoreKey] !== null && 
      essay[scoreKey] !== undefined && 
      typeof essay[scoreKey] === 'number'
    ).sort((a, b) => (b[scoreKey] || 0) - (a[scoreKey] || 0));
  }

  const rankedEssays: EssayWithRanks[] = [...essays];
  let currentRank = 1;
  
  validEssays.forEach((essay, index) => {
    let currentScore, prevScore;
    
    if (scoreKey === 'finalOverallScore' && modelId) {
      currentScore = essay.modelResults?.[modelId]?.finalOverallScore;
      prevScore = validEssays[index - 1]?.modelResults?.[modelId]?.finalOverallScore;
    } else {
      currentScore = essay[scoreKey];
      prevScore = validEssays[index - 1]?.[scoreKey];
    }
    
    if (index > 0 && currentScore !== prevScore) {
      currentRank = index + 1;
    }
    
    const essayIndex = rankedEssays.findIndex(e => e.id === essay.id);
    if (essayIndex !== -1) {
      if (scoreKey === 'finalOverallScore') {
        rankedEssays[essayIndex].aiRank = currentRank;
      } else {
        rankedEssays[essayIndex].manualRank = currentRank;
      }
    }
  });

  return rankedEssays;
};

// Statistical calculation functions
const calculateMAE = (essays: Essay[], modelId?: string): number => {
  const validEssays = getValidEssays(essays, modelId);
  if (validEssays.length === 0) return 0;
  
  const errors = validEssays.map(e => {
    const aiScore = modelId ? e.modelResults?.[modelId]?.finalOverallScore : e.finalOverallScore;
    return Math.abs((aiScore || 0) - (e.manualOverallScore || 0));
  });
  return errors.reduce((sum, err) => sum + err, 0) / validEssays.length;
};

const calculateBias = (essays: Essay[], modelId?: string): number => {
  const validEssays = getValidEssays(essays, modelId);
  if (validEssays.length === 0) return 0;
  
  const errors = validEssays.map(e => {
    const aiScore = modelId ? e.modelResults?.[modelId]?.finalOverallScore : e.finalOverallScore;
    return (aiScore || 0) - (e.manualOverallScore || 0);
  });
  return errors.reduce((sum, err) => sum + err, 0) / validEssays.length;
};

const calculateRMSE = (essays: Essay[], modelId?: string): number => {
  const validEssays = getValidEssays(essays, modelId);
  if (validEssays.length === 0) return 0;
  
  const squaredErrors = validEssays.map(e => {
    const aiScore = modelId ? e.modelResults?.[modelId]?.finalOverallScore : e.finalOverallScore;
    const diff = (aiScore || 0) - (e.manualOverallScore || 0);
    return diff * diff;
  });
  return Math.sqrt(squaredErrors.reduce((sum, err) => sum + err, 0) / validEssays.length);
};

const calculateTolerancePercentages = (essays: Essay[], modelId?: string): { within1: number; within2: number; within3: number } => {
  const validEssays = getValidEssays(essays, modelId);
  if (validEssays.length === 0) return { within1: 0, within2: 0, within3: 0 };
  
  const within1 = validEssays.filter(e => {
    const aiScore = modelId ? e.modelResults?.[modelId]?.finalOverallScore : e.finalOverallScore;
    return Math.abs((aiScore || 0) - (e.manualOverallScore || 0)) <= 1;
  }).length;
  const within2 = validEssays.filter(e => {
    const aiScore = modelId ? e.modelResults?.[modelId]?.finalOverallScore : e.finalOverallScore;
    return Math.abs((aiScore || 0) - (e.manualOverallScore || 0)) <= 2;
  }).length;
  const within3 = validEssays.filter(e => {
    const aiScore = modelId ? e.modelResults?.[modelId]?.finalOverallScore : e.finalOverallScore;
    return Math.abs((aiScore || 0) - (e.manualOverallScore || 0)) <= 3;
  }).length;
  
  return {
    within1: Math.round((within1 / validEssays.length) * 100),
    within2: Math.round((within2 / validEssays.length) * 100),
    within3: Math.round((within3 / validEssays.length) * 100)
  };
};

const calculatePearsonCorrelation = (essays: Essay[], modelId?: string): number => {
  const validEssays = getValidEssays(essays, modelId);
  if (validEssays.length < 2) return 0;

  const aiScores = validEssays.map(e => {
    const aiScore = modelId ? e.modelResults?.[modelId]?.finalOverallScore : e.finalOverallScore;
    return aiScore || 0;
  });
  const manualScores = validEssays.map(e => e.manualOverallScore || 0);

  const n = validEssays.length;
  const aiMean = aiScores.reduce((a, b) => a + b, 0) / n;
  const manualMean = manualScores.reduce((a, b) => a + b, 0) / n;

  let numerator = 0;
  let aiSumSquares = 0;
  let manualSumSquares = 0;

  for (let i = 0; i < n; i++) {
    const aiDiff = aiScores[i] - aiMean;
    const manualDiff = manualScores[i] - manualMean;
    
    numerator += aiDiff * manualDiff;
    aiSumSquares += aiDiff * aiDiff;
    manualSumSquares += manualDiff * manualDiff;
  }

  const denominator = Math.sqrt(aiSumSquares * manualSumSquares);
  return denominator === 0 ? 0 : numerator / denominator;
};

const calculateSpearmanCorrelation = (essays: Essay[], modelId?: string): number => {
  const validEssays = getValidEssays(essays, modelId);
  if (validEssays.length < 2) return 0;

  const aiRanks = validEssays
    .sort((a, b) => {
      const scoreA = modelId ? (a.modelResults?.[modelId]?.finalOverallScore || 0) : (a.finalOverallScore || 0);
      const scoreB = modelId ? (b.modelResults?.[modelId]?.finalOverallScore || 0) : (b.finalOverallScore || 0);
      return scoreB - scoreA;
    })
    .map((essay, index) => ({ id: essay.id, rank: index + 1 }));
    
  const manualRanks = validEssays
    .sort((a, b) => (b.manualOverallScore || 0) - (a.manualOverallScore || 0))
    .map((essay, index) => ({ id: essay.id, rank: index + 1 }));

  const n = validEssays.length;
  let sumDSquared = 0;

  validEssays.forEach(essay => {
    const aiRank = aiRanks.find(r => r.id === essay.id)?.rank || 0;
    const manualRank = manualRanks.find(r => r.id === essay.id)?.rank || 0;
    const d = aiRank - manualRank;
    sumDSquared += d * d;
  });

  return 1 - (6 * sumDSquared) / (n * (n * n - 1));
};

const calculateCompositeScore = (essays: Essay[], modelId?: string): number => {
  const validEssays = getValidEssays(essays, modelId);
  if (validEssays.length < 2) return 0;
  
  const mae = calculateMAE(essays, modelId);
  const tolerance = calculateTolerancePercentages(essays, modelId);
  
  // Simplified composite score calculation
  return Math.round(
    0.4 * tolerance.within2 +
    0.4 * Math.max(0, (1 - mae / 25)) * 100 +
    0.2 * tolerance.within1
  );
};

// UI Helper Components
const StatChip: React.FC<{ label: string; value: string | number; color?: 'green' | 'yellow' | 'red' | 'blue' | 'gray' }> = ({ 
  label, 
  value, 
  color = 'gray' 
}) => {
  const colorClasses = {
    green: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800',
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800',
    red: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800',
    blue: 'bg-primary/10 text-primary border-primary/20',
    gray: 'bg-muted text-muted-foreground border-border'
  };
  
  return (
    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${colorClasses[color]}`}>
      <span className="font-normal text-xs mr-1">{label}</span>
      <span>{value}</span>
    </div>
  );
};

interface CorrelationStatsProps {
  essays: Essay[];
  onViewEssayResults: (essay: Essay) => void;
  onRemarkEssay: (essay: Essay) => Promise<boolean>;
}

const CorrelationStats: React.FC<CorrelationStatsProps> = ({ essays, onViewEssayResults, onRemarkEssay }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  
  // Detect all available models across essays
  const allModelIds = new Set<string>();
  const modelInfo: Record<string, { name: string; essayCount: number }> = {};
  
  essays.forEach(essay => {
    if (essay.modelResults) {
      Object.keys(essay.modelResults).forEach(modelId => {
        allModelIds.add(modelId);
        if (!modelInfo[modelId]) {
          modelInfo[modelId] = {
            name: essay.modelResults![modelId].modelName,
            essayCount: 0
          };
        }
        if (essay.modelResults![modelId].finalOverallScore !== null && 
            essay.modelResults![modelId].finalOverallScore !== undefined) {
          modelInfo[modelId].essayCount++;
        }
      });
    }
  });
  
  const hasMultipleModels = allModelIds.size > 1;
  
  // Force reset to null if selected model no longer exists
  const validModelIds = Array.from(allModelIds);
  useEffect(() => {
    if (selectedModelId && !validModelIds.includes(selectedModelId)) {
      setSelectedModelId(null);
    }
  }, [selectedModelId, validModelIds.join(',')]);
  
  const validEssays = getValidEssays(essays, selectedModelId || undefined);
  const n = validEssays.length;
  
  // Safe model selection handler that always works
  const handleModelSelection = (modelId: string | null) => {
    setSelectedModelId(modelId);
  };
  
  // Always show model selector if multiple models exist, even with insufficient data
  const showModelSelector = hasMultipleModels;
  
  if (n < 2) {
    return (
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 rounded-lg mb-6">
        <h3 className="text-lg font-semibold mb-2">
          Statistical Analysis
          {selectedModelId && modelInfo[selectedModelId] && (
            <span className="text-sm font-normal ml-2 bg-blue-400 bg-opacity-30 px-2 py-1 rounded">
              {modelInfo[selectedModelId].name}
            </span>
          )}
        </h3>
        
        {/* Model selector even when insufficient data */}
        {showModelSelector && (
          <div className="mb-3">
            <label className="text-sm font-medium block mb-2">Analyze Model:</label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleModelSelection(null)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  selectedModelId === null 
                    ? 'bg-white text-blue-600' 
                    : 'bg-blue-400 bg-opacity-30 text-white hover:bg-opacity-50'
                }`}
              >
                All Models (Legacy)
              </button>
              {Array.from(allModelIds).map(modelId => (
                <button
                  key={modelId}
                  onClick={() => handleModelSelection(modelId)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    selectedModelId === modelId 
                      ? 'bg-white text-blue-600' 
                      : 'bg-blue-400 bg-opacity-30 text-white hover:bg-opacity-50'
                  }`}
                >
                  {modelInfo[modelId]?.name} ({modelInfo[modelId]?.essayCount})
                </button>
              ))}
            </div>
          </div>
        )}
        
        <p className="text-sm opacity-90">
          Need at least 2 essays with both AI and manual scores to calculate statistics.
          {selectedModelId && modelInfo[selectedModelId] && (
            <span className="block mt-1 text-xs opacity-75">
              Currently {modelInfo[selectedModelId].essayCount} essay{modelInfo[selectedModelId].essayCount !== 1 ? 's' : ''} 
              available for {modelInfo[selectedModelId].name}.
            </span>
          )}
        </p>
      </div>
    );
  }

  // Calculate statistics (model-specific if selectedModelId is set)
  const currentModelId = selectedModelId || undefined;
  const mae = calculateMAE(essays, currentModelId);
  const bias = calculateBias(essays, currentModelId);
  const rmse = calculateRMSE(essays, currentModelId);
  const tolerance = calculateTolerancePercentages(essays, currentModelId);
  const compositeScore = calculateCompositeScore(essays, currentModelId);

  // Color coding functions
  const getMAEColor = (mae: number) => {
    if (mae <= 2) return 'green';
    if (mae <= 4) return 'yellow';
    return 'red';
  };

  const getBiasColor = (bias: number) => {
    const absBias = Math.abs(bias);
    if (absBias <= 1) return 'green';
    if (absBias <= 2) return 'yellow';
    return 'red';
  };

  const getToleranceColor = (percent: number) => {
    if (percent >= 70) return 'green';
    if (percent >= 50) return 'yellow';
    return 'red';
  };

  const getCompositeColor = (score: number) => {
    if (score >= 80) return 'green';
    if (score >= 60) return 'yellow';
    return 'red';
  };

  return (
    <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 rounded-lg mb-6">
      <div className="cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">
            AI vs Manual Marking Analysis
            {selectedModelId && modelInfo[selectedModelId] && (
              <span className="text-sm font-normal ml-2 bg-blue-400 bg-opacity-30 px-2 py-1 rounded">
                {modelInfo[selectedModelId].name}
              </span>
            )}
          </h3>
          <div className="flex items-center gap-4">
            {n < 20 && (
              <span className="bg-yellow-200 text-yellow-800 text-xs font-medium px-2 py-1 rounded-full">
                Interpret with caution (n={n})
              </span>
            )}
            <div className="flex items-center text-sm">
              <span className="mr-2">Composite Score:</span>
              <StatChip 
                label="" 
                value={`${compositeScore}/100`}
                color={getCompositeColor(compositeScore)}
              />
            </div>
            <svg 
              className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </div>
        </div>
        
        {/* Model selector for multi-model scenarios */}
        {hasMultipleModels && (
          <div className="mb-3">
            <label className="text-sm font-medium block mb-2">Analyze Model:</label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedModelId(null);
                }}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  selectedModelId === null 
                    ? 'bg-white text-blue-600' 
                    : 'bg-blue-400 bg-opacity-30 text-white hover:bg-opacity-50'
                }`}
              >
                All Models (Legacy)
              </button>
              {Array.from(allModelIds).map(modelId => (
                <button
                  key={modelId}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedModelId(modelId);
                  }}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    selectedModelId === modelId 
                      ? 'bg-white text-blue-600' 
                      : 'bg-blue-400 bg-opacity-30 text-white hover:bg-opacity-50'
                  }`}
                >
                  {modelInfo[modelId]?.name} ({modelInfo[modelId]?.essayCount})
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Quick summary chips */}
        <div className="flex flex-wrap gap-2 text-sm">
          <StatChip label="Essays" value={n} color="blue" />
          <StatChip label="MAE" value={`${mae.toFixed(1)} pts`} color={getMAEColor(mae)} />
          <StatChip label="Bias" value={bias >= 0 ? `+${bias.toFixed(1)}` : bias.toFixed(1)} color={getBiasColor(bias)} />
          <StatChip label="RMSE" value={rmse.toFixed(1)} color="gray" />
          <StatChip label="±2 pts" value={`${tolerance.within2}%`} color={getToleranceColor(tolerance.within2)} />
        </div>
      </div>
      
      {isExpanded && (
        <div className="mt-6 space-y-6 border-t border-blue-400 pt-6">
          {/* Agreement Analysis */}
          <div>
            <h4 className="text-md font-semibold mb-3 flex items-center">
              <span className="w-6 h-6 bg-blue-400 text-blue-900 rounded-full flex items-center justify-center text-sm mr-2">1</span>
              Agreement Analysis
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <p className="text-xs opacity-75 uppercase">MAE (Mean Abs Error)</p>
                <p className="text-xl font-bold">{mae.toFixed(2)} pts</p>
                <p className="text-xs opacity-75">({Math.round((mae/25)*100)}% of max)</p>
              </div>
              <div className="text-center">
                <p className="text-xs opacity-75 uppercase">Bias (AI - Human)</p>
                <p className="text-xl font-bold">{bias >= 0 ? '+' : ''}{bias.toFixed(2)}</p>
                <p className="text-xs opacity-75">{bias > 0 ? 'AI over-scores' : bias < 0 ? 'AI under-scores' : 'No bias'}</p>
              </div>
              <div className="text-center">
                <p className="text-xs opacity-75 uppercase">RMSE</p>
                <p className="text-xl font-bold">{rmse.toFixed(2)}</p>
                <p className="text-xs opacity-75">Highlights big misses</p>
              </div>
              <div className="text-center">
                <p className="text-xs opacity-75 uppercase">Tolerance</p>
                <p className="text-sm">±1: {tolerance.within1}%</p>
                <p className="text-sm">±2: {tolerance.within2}%</p>
                <p className="text-sm">±3: {tolerance.within3}%</p>
              </div>
            </div>
          </div>

          {/* Correlation Analysis */}
          <div>
            <h4 className="text-md font-semibold mb-3 flex items-center">
              <span className="w-6 h-6 bg-blue-400 text-blue-900 rounded-full flex items-center justify-center text-sm mr-2">2</span>
              Correlation Analysis
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="text-center">
                <p className="text-xs opacity-75 uppercase">Pearson Correlation</p>
                <p className="text-xl font-bold">{calculatePearsonCorrelation(essays, currentModelId).toFixed(3)}</p>
                <p className="text-xs opacity-75">Linear relationship</p>
              </div>
              <div className="text-center">
                <p className="text-xs opacity-75 uppercase">Spearman Correlation</p>
                <p className="text-xl font-bold">{calculateSpearmanCorrelation(essays, currentModelId).toFixed(3)}</p>
                <p className="text-xs opacity-75">Rank-based relationship</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// New EssayCard component with accordion functionality
interface EssayCardProps {
  essay: EssayWithRanks;
  displayDate: string;
  modelResults: Record<string, any>;
  modelKeys: string[];
  isAdmin: boolean;
  manualScoringEnabled: boolean;
  primaryAiScore: number | null | undefined;
  manualScore: number | null | undefined;
  selectedEssayIds: Set<string>;
  selectedModelFilters: Set<string>;
  isLoading: boolean;
  onSelectEssay: (essayId: string) => void;
  onViewEssayResults: (essay: Essay) => void;
  onSingleEssayRemark: (essay: Essay) => void;
  onDeleteEssay: (essayId: string, studentName: string) => void;
  onDeleteModelResult?: (essayId: string, modelId: string) => void;
}

const EssayCard: React.FC<EssayCardProps> = ({
  essay,
  displayDate,
  modelResults,
  modelKeys,
  isAdmin,
  manualScoringEnabled,
  primaryAiScore,
  manualScore,
  selectedEssayIds,
  selectedModelFilters,
  isLoading,
  onSelectEssay,
  onViewEssayResults,
  onSingleEssayRemark,
  onDeleteEssay,
  onDeleteModelResult
}) => {
  const [isTeacherExpanded, setIsTeacherExpanded] = useState(false);
  const [expandedModels, setExpandedModels] = useState<Set<string>>(new Set());
  const [isFeedbackExpanded, setIsFeedbackExpanded] = useState(false);
  const visibleModelKeys = isAdmin ? modelKeys : modelKeys.slice(0, 1);
  const showManualSections = isAdmin || manualScoringEnabled;
  const primaryModelId = visibleModelKeys[0];
  const primaryModelResult = primaryModelId ? modelResults[primaryModelId] : null;
  const primaryFeedback = primaryModelResult?.feedback || essay.feedback || null;

  const formatRunDate = (value: any) => {
    if (!value) return 'No date';
    try {
      if (value && typeof value.toDate === 'function') {
        return value.toDate().toLocaleDateString('en-AU', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      }

      if (value && typeof value === 'object' && 'seconds' in value && 'nanoseconds' in value) {
        const date = new Date(value.seconds * 1000 + (value.nanoseconds as number) / 1000000);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-AU', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          });
        }
      }

      if (typeof value === 'string') {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-AU', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          });
        }
      }

      if (typeof value === 'number') {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-AU', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          });
        }
      }

      if (value instanceof Date && !isNaN(value.getTime())) {
        return value.toLocaleDateString('en-AU', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    } catch (error) {
      console.warn('Error parsing lastRunAt date:', value, error);
    }
    return 'No date';
  };

  const toggleModelExpansion = (modelId: string) => {
    const newExpanded = new Set(expandedModels);
    if (newExpanded.has(modelId)) {
      newExpanded.delete(modelId);
    } else {
      newExpanded.add(modelId);
    }
    setExpandedModels(newExpanded);
  };

  const handleDeleteModelResult = async (e: React.MouseEvent, essayId: string, modelId: string) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (window.confirm('Are you sure you want to delete this AI marking result?')) {
      if (onDeleteModelResult) {
        onDeleteModelResult(essayId, modelId);
      }
    }
  };

  const getDifferenceDisplay = (difference: number) => {
    const absDiff = Math.abs(difference);
    let color = 'text-gray-600';
    let bgColor = 'bg-gray-100';
    let symbol = '';
    
    if (absDiff <= 1) {
      color = 'text-green-700';
      bgColor = 'bg-green-100';
      symbol = '✓';
    } else if (absDiff <= 3) {
      color = 'text-yellow-700';
      bgColor = 'bg-yellow-100';
      symbol = '~';
    } else {
      color = 'text-red-700';
      bgColor = 'bg-red-100';
      symbol = '!';
    }
    
    return (
      <div className={`inline-flex items-center px-3 py-2 rounded-lg font-bold text-xl ${color} ${bgColor}`}>
        <span className="mr-2 text-lg">{symbol}</span>
        {difference > 0 ? `+${difference}` : difference}
      </div>
    );
  };

  const renderFeedbackAccordion = () => (
    <div className="border-t border-gray-100">
      <button
        onClick={() => setIsFeedbackExpanded(!isFeedbackExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-sky-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4-.83L3 20l1.39-3.724C3.518 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <div>
            <p className="font-semibold text-gray-900">AI Feedback</p>
            <p className="text-xs text-gray-600">AI generated feedback</p>
          </div>
        </div>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform ${isFeedbackExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </button>

      {isFeedbackExpanded && (
        <div className="p-4 bg-gray-50">
          {primaryFeedback ? (
            <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
              {primaryFeedback}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No AI feedback available yet.</p>
          )}
        </div>
      )}
    </div>
  );

  if (!showManualSections) {
    return (
      <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-200 border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-slate-50 to-gray-50 p-4 border-b border-gray-100">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-sky focus:ring-sky mt-1"
                checked={selectedEssayIds.has(essay.id)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  e.stopPropagation();
                  onSelectEssay(essay.id);
                }}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                aria-labelledby={`essay-name-${essay.id}`}
              />
              <div>
                <h3 id={`essay-name-${essay.id}`} className="text-2xl font-extrabold text-gray-900">
                  {essay.studentName}
                </h3>
                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 mt-1">
                  <span className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                    {displayDate}
                  </span>
                  {isAdmin && primaryModelResult?.modelName && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full bg-purple-50 text-purple-700 text-xs font-semibold">
                      {primaryModelResult.modelName}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  onViewEssayResults(essay);
                }}
                variant="secondary"
                size="sm"
                className="text-xs"
              >
                View Details
              </Button>
              <button
                onClick={() => onSingleEssayRemark(essay)}
                disabled={isLoading}
                className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                Re-mark
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <p className="text-xs font-semibold text-gray-600 uppercase mb-1">AI Overall</p>
              <div className="text-3xl font-bold text-gray-900">
                {primaryModelResult?.finalOverallScore ?? 'Not marked'}
              </div>
              {primaryModelResult?.finalOverallBand && (
                <p className="text-sm text-gray-700 mt-1">
                  Band: <span className="font-semibold text-purple-700">{primaryModelResult.finalOverallBand}</span>
                </p>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Last run: {primaryModelResult ? formatRunDate(primaryModelResult.lastRunAt) : 'No runs yet'}
              </p>
            </div>
            <div className="p-4 border border-gray-200 rounded-lg bg-white shadow-inner">
              <p className="text-xs font-semibold text-gray-600 uppercase mb-2">Band Breakdown</p>
              {primaryModelResult?.consolidatedMarkingResults && primaryModelResult.consolidatedMarkingResults.length > 0 ? (
                <div className="space-y-2">
                  {primaryModelResult.consolidatedMarkingResults.map((criterion: any) => (
                    <div key={criterion.criterionName} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">{criterion.criterionName}</span>
                      <span className="font-semibold text-purple-700">{criterion.selectedBand || '—'}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No band scores available yet.</p>
              )}
            </div>
          </div>
        </div>
        {renderFeedbackAccordion()}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-soft hover:shadow-medium transition-all duration-200 border border-gray-100 overflow-hidden">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-slate-50 to-gray-50 p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-sky focus:ring-sky"
              checked={selectedEssayIds.has(essay.id)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                e.stopPropagation();
                onSelectEssay(essay.id);
              }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              aria-labelledby={`essay-name-${essay.id}`}
            />
            <div>
              <h3 id={`essay-name-${essay.id}`} className="text-lg font-bold text-gray-900">{essay.studentName}</h3>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mt-1">
                <div className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2v12a2 2 0 002 2z"></path>
                  </svg>
                  {displayDate}
                </div>
                {isAdmin && visibleModelKeys.length > 0 && (
                  <div className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2z"></path>
                    </svg>
                    {visibleModelKeys.length} AI model{visibleModelKeys.length !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onViewEssayResults(essay);
              }}
              variant="secondary"
              size="sm"
              className="text-xs"
            >
              View Details
            </Button>
            <button
              onClick={() => onSingleEssayRemark(essay)}
              disabled={isLoading}
              className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              Re-mark
            </button>
          </div>
        </div>
      </div>

      {/* Teacher Assessment Section - Collapsible */}
      <div className="border-b border-gray-100">
        <button
          onClick={() => setIsTeacherExpanded(!isTeacherExpanded)}
          className="w-full px-4 py-3 flex items-center justify-between bg-blue-50 hover:bg-blue-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
              </svg>
            </div>
            <div className="text-left">
              <div className="font-semibold text-gray-900 flex items-center gap-2">
                Manual Mark: {manualScore !== null && manualScore !== undefined ? manualScore : 'Not marked'}
                {essay.manualRank && (
                  <span className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded-full">
                    Rank #{essay.manualRank}
                  </span>
                )}
              </div>
            </div>
          </div>
          <svg 
            className={`w-5 h-5 text-gray-500 transition-transform ${isTeacherExpanded ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        </button>
        
        {isTeacherExpanded && (
          <div className="p-4 bg-gray-50 border-t border-gray-200">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Overall Score:</span>
                <span className="ml-2">{essay.manualOverallScore !== null && essay.manualOverallScore !== undefined ? essay.manualOverallScore : 'Not marked'}</span>
              </div>
                <div>
                  <span className="font-medium text-gray-700">Band Scores:</span>
                  <div className="mt-1 space-y-1">
                    {essay.manualMarkingResults && essay.manualMarkingResults.length > 0 ? (
                      essay.manualMarkingResults.map((criterion: any) => (
                        <div key={criterion.criterionName} className="text-xs flex justify-between items-center">
                          <span>{criterion.criterionName}:</span>
                          <span className="font-bold text-blue-700 ml-2">{criterion.band || 'Not marked'}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-xs text-gray-500">No band scores available</div>
                    )}
                  </div>
                </div>
            </div>
          </div>
        )}
      </div>

      {renderFeedbackAccordion()}

      {/* AI Model Results - Collapsible */}
      {visibleModelKeys.length > 0 && visibleModelKeys.map((modelId) => {
        const modelResult = modelResults[modelId];
        if (!modelResult) return null;
        
        const difference = manualScore !== null && manualScore !== undefined && modelResult.finalOverallScore !== null && modelResult.finalOverallScore !== undefined
          ? modelResult.finalOverallScore - manualScore
          : null;
        
        const isExpanded = expandedModels.has(modelId);
        
        return (
          <div key={modelId} className="border-b border-gray-100 last:border-b-0">
            <button
              onClick={() => toggleModelExpansion(modelId)}
              className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2z"></path>
                  </svg>
                </div>
                <div className="text-left">
                  <div className="font-semibold text-gray-900 flex items-center gap-2">
                    AI Mark: {modelResult.finalOverallScore}
                    {isAdmin && (
                      <span className="text-xs bg-purple-200 text-purple-800 px-2 py-1 rounded-full">
                        {modelResult.modelName}
                      </span>
                    )}
                    {essay.aiRank && (
                      <span className="text-xs bg-gray-200 text-gray-800 px-2 py-1 rounded-full">
                        Rank #{essay.aiRank}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    Last run: {(() => {
                      if (!modelResult.lastRunAt) return 'No date';
                      try {
                        // Handle Firebase Timestamp objects
                        if (modelResult.lastRunAt && typeof modelResult.lastRunAt.toDate === 'function') {
                          return modelResult.lastRunAt.toDate().toLocaleDateString('en-AU', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          });
                        }
                        
                        // Handle Firestore timestamp objects with seconds field
                        if (modelResult.lastRunAt && typeof modelResult.lastRunAt === 'object' && 
                            'seconds' in modelResult.lastRunAt && 'nanoseconds' in modelResult.lastRunAt) {
                          const date = new Date(modelResult.lastRunAt.seconds * 1000 + modelResult.lastRunAt.nanoseconds / 1000000);
                          if (!isNaN(date.getTime())) {
                            return date.toLocaleDateString('en-AU', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            });
                          }
                        }
                        
                        // Handle ISO string dates
                        if (typeof modelResult.lastRunAt === 'string') {
                          const date = new Date(modelResult.lastRunAt);
                          if (!isNaN(date.getTime())) {
                            return date.toLocaleDateString('en-AU', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            });
                          }
                        }
                        
                        // Handle numeric timestamps (milliseconds since epoch)
                        if (typeof modelResult.lastRunAt === 'number') {
                          const date = new Date(modelResult.lastRunAt);
                          if (!isNaN(date.getTime())) {
                            return date.toLocaleDateString('en-AU', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            });
                          }
                        }
                        
                        // Handle Date objects
                        if (modelResult.lastRunAt instanceof Date) {
                          if (!isNaN(modelResult.lastRunAt.getTime())) {
                            return modelResult.lastRunAt.toLocaleDateString('en-AU', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            });
                          }
                        }
                        
                        return 'No date';
                      } catch (error) {
                        console.warn('Error parsing lastRunAt date:', modelResult.lastRunAt, error);
                        return 'No date';
                      }
                    })()}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {difference !== null && getDifferenceDisplay(difference)}
                <button
                  onClick={(e) => handleDeleteModelResult(e, essay.id, modelId)}
                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                  title="Delete AI marking result"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
                <svg 
                  className={`w-5 h-5 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </div>
            </button>
            
            {isExpanded && (
              <div className="p-4 bg-purple-50 border-t border-purple-200">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Overall Score:</span>
                    <span className="ml-2">{modelResult.finalOverallScore !== null && modelResult.finalOverallScore !== undefined ? modelResult.finalOverallScore : 'Not marked'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Band Scores:</span>
                    <div className="mt-1 space-y-1">
                      {modelResult.consolidatedMarkingResults && modelResult.consolidatedMarkingResults.length > 0 ? (
                        modelResult.consolidatedMarkingResults.map((criterion: any) => (
                          <div key={criterion.criterionName} className="text-xs flex justify-between items-center">
                            <span>{criterion.criterionName}:</span>
                            <span className="font-bold text-purple-700 ml-2">{criterion.selectedBand || 'Not available'}</span>
                          </div>
                        ))
                      ) : (
                        <div className="text-xs text-gray-500">No band scores available</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

interface AssessmentSelectionProps {
  assessments: Assessment[];
  isLoading: boolean;
  isAdmin: boolean;
  manualScoringEnabled: boolean;
  onSelectAssessment: (assessment: Assessment) => Promise<void>;
  onCreateNew: () => void;
  onEditAssessment: (assessmentId: any) => void;
  onDeleteAssessment: (assessmentId: string, assessmentName: string) => Promise<void>;
  onArchiveAssessment: (assessmentId: string, assessmentName: string) => Promise<void>;
  onUnarchiveAssessment?: (assessmentId: string, assessmentName: string) => Promise<void>;
  showingArchived?: boolean;
  onToggleArchiveView?: () => void;
  viewingAssessment: Assessment | null;
  essaysForViewingAssessment: Essay[];
  onMarkNewEssay: (assessment: Assessment) => void;
  onViewEssayResults: (essay: Essay) => void;
  onRemarkEssay: (essay: Essay) => Promise<boolean>;
  onRequireCreditConfirmation: (creditsRequired: number, actionDescription: string, onConfirm: () => void) => void;
  onDeleteEssay: (essayId: string, studentName: string) => Promise<void>;
  onBatchDeleteEssays?: (essayIds: string[], count: number) => Promise<void>;
  onDeleteModelResult?: (essayId: string, modelId: string) => void;
  sortOrder: string;
  onSortOrderChange: (order: string) => void;
  setCurrentStep: React.Dispatch<React.SetStateAction<AppStep>>;
  onBackToAssessments?: () => void;
}

const AssessmentSelection: React.FC<AssessmentSelectionProps> = ({
  assessments,
  isLoading,
  isAdmin,
  manualScoringEnabled,
  onSelectAssessment,
  onCreateNew,
  onEditAssessment,
  onDeleteAssessment,
  onArchiveAssessment,
  onUnarchiveAssessment,
  showingArchived = false,
  onToggleArchiveView,
  viewingAssessment: currentAssessment,
  essaysForViewingAssessment: essays,
  onMarkNewEssay,
  onViewEssayResults,
  onRemarkEssay,
  onRequireCreditConfirmation,
  onDeleteEssay,
  onBatchDeleteEssays,
  onDeleteModelResult,
  sortOrder,
  onSortOrderChange,
  setCurrentStep,
  onBackToAssessments
}) => {
  const {
    filters,
    setFilter,
    clearFilters,
    customValues,
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    selectedModelFilters,
    setSelectedModelFilters,
    searchQuery,
    setSearchQuery,
  } = useAssessmentStore();

  const [selectedEssayIds, setSelectedEssayIds] = useState<Set<string>>(new Set());
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [isRemarkModalOpen, setIsRemarkModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedBatchModelIds, setSelectedBatchModelIds] = useState<string[]>([]);
  const [isSaveTemplateModalOpen, setIsSaveTemplateModalOpen] = useState(false);
  const allowManualScoring = isAdmin || manualScoringEnabled;

  // Legacy assessments may only have a free-text rubric string; a template needs
  // structured criteria with bands plus a total mark.
  const templateCriteriaFromAssessment = useMemo<RubricTemplateCriterion[]>(() => {
    if (!currentAssessment?.criteria) return [];
    return currentAssessment.criteria
      .filter(c => c.name && Array.isArray(c.bands) && c.bands.length > 0)
      .map(c => ({
        name: c.name,
        weight: Number(c.weight) || 0,
        bands: (c.bands || []).map(b => ({
          min: Number(b.min) || 0,
          max: Number(b.max) || 0,
          descriptor: b.descriptor || ''
        }))
      }));
  }, [currentAssessment]);

  const canSaveRubricAsTemplate =
    templateCriteriaFromAssessment.length > 0 && Number(currentAssessment?.totalMarks) > 0;

  const buildOptions = (base: string[], custom: string[], derived: Array<string | undefined | null>) => {
    return Array.from(
      new Set([
        ...base,
        ...custom,
        ...derived.filter((value): value is string => Boolean(value))
      ])
    );
  };

  const subjectOptions = useMemo(
    () => buildOptions(
      ['Modern History', 'Ancient History', 'History Extension', 'History'],
      customValues?.subjects || [],
      assessments.map(a => a.subject)
    ),
    [assessments, customValues?.subjects]
  );

  const topicOptions = useMemo(
    () => buildOptions(
      ['Power and Authority', 'Russia and the Soviet Union', 'Cultural Revolution to Tiananmen Square'],
      customValues?.topics || [],
      assessments.map(a => a.topic)
    ),
    [assessments, customValues?.topics]
  );

  const yearGroupOptions = useMemo(
    () => buildOptions(
      ['Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11', 'Year 12'],
      customValues?.yearGroups || [],
      assessments.map(a => a.yearGroup || a.yearLevel)
    ),
    [assessments, customValues?.yearGroups]
  );

  const assessmentTypeOptions = useMemo(
    () => buildOptions(
      ['Essay', 'Source Analysis', 'Research Task', 'Essay Using Sources', 'Short Answer', 'Major Work', 'Combination'],
      customValues?.assessmentTypes || [],
      assessments.map(a => a.assessmentType)
    ),
    [assessments, customValues?.assessmentTypes]
  );

  const examConditionOptions = useMemo(
    () => buildOptions(
      ['In Class', 'Take Home', 'Open Book'],
      customValues?.examConditions || [],
      assessments.map(a => a.examCondition)
    ),
    [assessments, customValues?.examConditions]
  );

  const filteredAssessments = useMemo(() => {
    const matches = (filterValue: string | null, assessmentValue?: string | null) => {
      if (!filterValue) return true;
      if (!assessmentValue) return false;
      return assessmentValue.toLowerCase() === filterValue.toLowerCase();
    };

    return assessments.filter(assessment => 
      matches(filters.subject, assessment.subject) &&
      matches(filters.topic, assessment.topic) &&
      matches(filters.yearGroup, assessment.yearGroup || assessment.yearLevel) &&
      matches(filters.year, assessment.year) &&
      matches(filters.examCondition, assessment.examCondition) &&
      matches(filters.assessmentType, assessment.assessmentType)
    );
  }, [assessments, filters]);

  // Handle essay selection
  const handleSelectEssay = (essayId: string) => {
    const newSelected = new Set(selectedEssayIds);
    if (newSelected.has(essayId)) {
      newSelected.delete(essayId);
    } else {
      newSelected.add(essayId);
    }
    setSelectedEssayIds(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedEssayIds.size === essays.length) {
      setSelectedEssayIds(new Set());
    } else {
      setSelectedEssayIds(new Set(essays.map(e => e.id)));
    }
  };

  // Single essay remark
  const handleSingleEssayRemark = async (essay: Essay): Promise<boolean> => {
    return await onRemarkEssay(essay);
  };

  // Batch remark
  const openBatchWithModels = (modelIds: string[]) => {
    setSelectedBatchModelIds(modelIds);
    setIsBatchModalOpen(true);
  };

  const handleBatchRemark = () => {
    if (selectedEssayIds.size === 0) {
      alert('Please select at least one essay to remark.');
      return;
    }
    if (isAdmin) {
      setIsRemarkModalOpen(true);
      return;
    }

    // Non-admins always use the default model without extra UI, but still confirm credits
    const modelsToUse = [DEFAULT_MODEL_ID];
    const essayCount = selectedEssayIds.size;
    const totalCreditsNeeded = essayCount * modelsToUse.length; // 1 credit per model per essay
    const actionDescription = `Remark ${essayCount} essay${essayCount !== 1 ? 's' : ''} using ${modelsToUse.length} AI model${modelsToUse.length !== 1 ? 's' : ''}`;

    onRequireCreditConfirmation(totalCreditsNeeded, actionDescription, () => openBatchWithModels(modelsToUse));
  };

  // Batch delete
  const handleBatchDelete = async () => {
    if (selectedEssayIds.size === 0) {
      alert('Please select at least one essay to delete.');
      return;
    }
    if (!onBatchDeleteEssays) return;
    const ids = Array.from(selectedEssayIds);
    await onBatchDeleteEssays(ids, ids.length);
    // Selection is cleared by the parent after a confirmed delete; clear locally too.
    setSelectedEssayIds(new Set());
  };

  useEffect(() => {
    if (!allowManualScoring && (sortBy === 'manualScore' || sortBy === 'difference')) {
      setSortBy('aiScore');
    }
  }, [allowManualScoring, setSortBy, sortBy]);

  // Filter and sort essays
  const processedEssays = essays
    .filter(essay => {
      if (searchQuery) {
        return essay.studentName.toLowerCase().includes(searchQuery.toLowerCase());
      }
      return true;
    })
    .map(essay => {
      const modelResults: Record<string, any> = {};
      const modelKeys: string[] = [];

      // Handle both new modelResults structure and legacy fields
      if (essay.modelResults) {
        Object.entries(essay.modelResults).forEach(([modelId, result]) => {
          if (selectedModelFilters.size === 0 || selectedModelFilters.has(modelId)) {
            modelResults[modelId] = result;
            modelKeys.push(modelId);
          }
        });
      }

      // Get primary AI score (for sorting purposes)
      const primaryAiScore = modelKeys.length > 0 
        ? modelResults[modelKeys[0]]?.finalOverallScore 
        : essay.finalOverallScore;

      const manualScore = essay.manualOverallScore;
      const displayDate = essay.submissionDate 
        ? new Date(essay.submissionDate).toLocaleDateString()
        : 'No date';

      return {
        ...essay,
        modelResults,
        modelKeys,
        primaryAiScore,
        manualScore,
        displayDate
      };
    })
    .sort((a, b) => {
      const direction = sortDirection === 'desc' ? -1 : 1;
      
      switch (sortBy) {
        case 'studentName':
          return direction * a.studentName.localeCompare(b.studentName);
        case 'submissionDate':
          const dateA = a.submissionDate ? new Date(a.submissionDate).getTime() : 0;
          const dateB = b.submissionDate ? new Date(b.submissionDate).getTime() : 0;
          return direction * (dateA - dateB);
        case 'manualScore':
          const manualA = a.manualScore ?? -1;
          const manualB = b.manualScore ?? -1;
          return direction * (manualA - manualB);
        case 'aiScore':
          const aiA = a.primaryAiScore ?? -1;
          const aiB = b.primaryAiScore ?? -1;
          return direction * (aiA - aiB);
        case 'difference':
          const diffA = (a.primaryAiScore !== null && a.manualScore !== null) 
            ? Math.abs(a.primaryAiScore - a.manualScore) : -1;
          const diffB = (b.primaryAiScore !== null && b.manualScore !== null) 
            ? Math.abs(b.primaryAiScore - b.manualScore) : -1;
          return direction * (diffA - diffB);
        default:
          return 0;
      }
    });

  const rankedEssays = calculateRankings(processedEssays, 'manualOverallScore');

  // Get all available models for filtering
  const allModels = new Set<string>();
  const modelInfo: Record<string, { name: string; count: number }> = {};
  
  essays.forEach(essay => {
    if (essay.modelResults) {
      Object.entries(essay.modelResults).forEach(([modelId, result]) => {
        allModels.add(modelId);
        if (!modelInfo[modelId]) {
          modelInfo[modelId] = {
            name: result.modelName,
            count: 0
          };
        }
        modelInfo[modelId].count++;
      });
    }
  });

  const handleModelFilterToggle = (modelId: string) => {
    const newFilters = new Set(selectedModelFilters);
    if (newFilters.has(modelId)) {
      newFilters.delete(modelId);
    } else {
      newFilters.add(modelId);
    }
    setSelectedModelFilters(newFilters);
  };

  // Show assessment list when no assessment is selected
  if (!currentAssessment) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold text-blue-900">Choose Assessment Task</h2>
            <p className="text-gray-600 mt-1">
              Showing {filteredAssessments.length} of {assessments.length} assessment{assessments.length !== 1 ? 's' : ''} available
            </p>
          </div>
          <div className="flex items-center gap-3">
            {onToggleArchiveView && (
              <button
                onClick={onToggleArchiveView}
                className="text-gray-600 hover:text-gray-800 text-sm font-medium flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8l4 4 4-4"></path>
                </svg>
                {showingArchived ? 'Show Active' : 'Show Archived'}
              </button>
            )}
            <Button onClick={onCreateNew} className="bg-blue-600 hover:bg-blue-700">
              Create New Assessment
            </Button>
          </div>
        </div>

        {/* Filter Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Filter Assessments</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={filters.subject || ''}
                onChange={(e) => setFilter('subject', e.target.value || null)}
              >
                <option value="">All Subjects</option>
                {subjectOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Topic</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={filters.topic || ''}
                onChange={(e) => setFilter('topic', e.target.value || null)}
              >
                <option value="">All Topics</option>
                {topicOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Year Group</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={filters.yearGroup || ''}
                onChange={(e) => setFilter('yearGroup', e.target.value || null)}
              >
                <option value="">All Year Groups</option>
                {yearGroupOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Assessment Type</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={filters.assessmentType || ''}
                onChange={(e) => setFilter('assessmentType', e.target.value || null)}
              >
                <option value="">All Types</option>
                {assessmentTypeOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-between items-center mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Exam Condition</label>
              <select
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={filters.examCondition || ''}
                onChange={(e) => setFilter('examCondition', e.target.value || null)}
              >
                <option value="">All Conditions</option>
                {examConditionOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <button
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              type="button"
              onClick={clearFilters}
            >
              Clear Filters
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading assessments...</p>
          </div>
        ) : assessments.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <div className="max-w-md mx-auto">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Assessments Yet</h3>
              <p className="text-gray-600 mb-4">Create your first assessment to start marking essays with AI assistance.</p>
              <Button onClick={onCreateNew} className="bg-blue-600 hover:bg-blue-700">
                Create Your First Assessment
              </Button>
            </div>
          </div>
        ) : filteredAssessments.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <div className="max-w-md mx-auto space-y-3">
              <svg className="w-16 h-16 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 13h6m-6-4h6m2 10H7a2 2 0 01-2-2V7a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2z"></path>
              </svg>
              <div>
                <h3 className="text-lg font-medium text-gray-900">No assessments match your filters</h3>
                <p className="text-gray-600">Try adjusting the filters to see more assessments.</p>
              </div>
              <div>
                <Button onClick={clearFilters} variant="secondary">Reset Filters</Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {filteredAssessments.map((assessment) => (
              <AssessmentCard
                key={assessment.id}
                assessment={assessment}
                onSelect={() => onSelectAssessment(assessment)}
                onEdit={() => onEditAssessment(assessment.id)}
                onArchive={() => onArchiveAssessment(assessment.id, `Assessment ${assessment.assessmentNumber || assessment.name || 'Unknown'}`)}
                onUnarchive={onUnarchiveAssessment ? () => onUnarchiveAssessment(assessment.id, assessment.name) : undefined}
                onDelete={() => onDeleteAssessment(assessment.id, assessment.name)}
                showingArchived={showingArchived}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Assessment Header using the new shadcn component */}
      <AssessmentHeader
        assessment={currentAssessment}
        essayCount={essays.length}
        selectedCount={selectedEssayIds.size}
        onMarkNewEssay={() => onMarkNewEssay(currentAssessment)}
        onBatchRemark={handleBatchRemark}
        onBack={onBackToAssessments}
        onSaveAsTemplate={() => setIsSaveTemplateModalOpen(true)}
        canSaveAsTemplate={canSaveRubricAsTemplate}
        isLoading={isLoading}
      />

      <SaveRubricTemplateModal
        isOpen={isSaveTemplateModalOpen}
        onClose={() => setIsSaveTemplateModalOpen(false)}
        defaultName={currentAssessment.name || ''}
        totalMarks={Number(currentAssessment.totalMarks) || 0}
        criteria={templateCriteriaFromAssessment}
        sourceAssessmentId={currentAssessment.id}
      />

      {/* Correlation Statistics */}
      {isAdmin && (
        <CorrelationStats
          essays={essays}
          onViewEssayResults={onViewEssayResults}
          onRemarkEssay={handleSingleEssayRemark}
        />
      )}

      {/* Controls */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="flex-1 min-w-64">
            <input
              type="text"
              placeholder="Search students..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          {/* Sort */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Sort by:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="studentName">Student Name</option>
              <option value="submissionDate">Submission Date</option>
              <option value="aiScore">AI Score</option>
              {allowManualScoring && <option value="manualScore">Manual Score</option>}
              {allowManualScoring && <option value="difference">Score Difference</option>}
            </select>
            <button
              onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
              className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg border border-gray-300 transition-colors flex items-center gap-1"
              title={sortDirection === 'asc' ? 'Currently ascending - click to sort descending (highest to lowest)' : 'Currently descending - click to sort ascending (lowest to highest)'}
            >
              <svg className={`w-4 h-4 transition-transform ${sortDirection === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l4-4 4 4M8 15l4 4 4-4"></path>
              </svg>
              <span className="text-xs">
                {sortDirection === 'asc' ? 'Low to High' : 'High to Low'}
              </span>
            </button>
          </div>
        </div>

        {/* Model Filters */}
        {isAdmin && allModels.size > 0 && (
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">Filter AI Models:</label>
            <div className="flex flex-wrap gap-2">
              {Array.from(allModels).map(modelId => (
                <button
                  key={modelId}
                  onClick={() => handleModelFilterToggle(modelId)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    selectedModelFilters.has(modelId) || selectedModelFilters.size === 0
                      ? 'bg-blue-100 text-blue-800 border-2 border-blue-300'
                      : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:border-gray-300'
                  }`}
                >
                  {modelInfo[modelId]?.name} ({modelInfo[modelId]?.count})
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Select All + batch actions */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              checked={selectedEssayIds.size === essays.length && essays.length > 0}
              onChange={handleSelectAll}
            />
            <label className="text-sm font-medium text-gray-700">
              Select All ({selectedEssayIds.size}/{essays.length})
            </label>
          </div>
          {onBatchDeleteEssays && selectedEssayIds.size > 0 && (
            <button
              onClick={handleBatchDelete}
              disabled={isLoading}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 hover:border-red-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Delete the selected essays"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete Selected ({selectedEssayIds.size})
            </button>
          )}
        </div>
      </div>

      {/* Essay Cards */}
      <div className="space-y-4">
        {processedEssays.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-600">No essays found matching your criteria.</p>
          </div>
        ) : (
          processedEssays.map((essay) => (
            <EssayCard
              key={essay.id}
              essay={essay}
              displayDate={essay.displayDate}
              modelResults={essay.modelResults}
              modelKeys={essay.modelKeys}
              isAdmin={isAdmin}
              manualScoringEnabled={allowManualScoring}
              primaryAiScore={essay.primaryAiScore}
              manualScore={essay.manualScore}
              selectedEssayIds={selectedEssayIds}
              selectedModelFilters={selectedModelFilters}
              isLoading={isLoading}
              onSelectEssay={handleSelectEssay}
              onViewEssayResults={onViewEssayResults}
              onSingleEssayRemark={handleSingleEssayRemark}
              onDeleteEssay={onDeleteEssay}
              onDeleteModelResult={onDeleteModelResult}
            />
          ))
        )}
      </div>

      {/* Batch Remark Modal */}
      {isAdmin && (
        <RemarkModelsSelectionModal
          isOpen={isRemarkModalOpen}
          onClose={() => setIsRemarkModalOpen(false)}
          onConfirm={(selectedModelIds: string[]) => {
            console.log('Selected models for batch remark:', selectedModelIds);
            const modelIds = selectedModelIds.length > 0 ? selectedModelIds : [DEFAULT_MODEL_ID];
            const essayCount = selectedEssayIds.size;
            const totalCreditsNeeded = essayCount * modelIds.length; // 1 credit per model per essay
            const actionDescription = `Remark ${essayCount} essay${essayCount !== 1 ? 's' : ''} using ${modelIds.length} AI model${modelIds.length !== 1 ? 's' : ''}`;

            onRequireCreditConfirmation(totalCreditsNeeded, actionDescription, () => {
              setIsRemarkModalOpen(false);
              openBatchWithModels(modelIds);
            });
          }}
          essayCount={selectedEssayIds.size}
        />
      )}

      {/* Batch Progress Modal */}
      <BatchRemarkProgressModal
        isOpen={isBatchModalOpen}
        onClose={() => {
          setIsBatchModalOpen(false);
          setSelectedEssayIds(new Set()); // Clear selected essays after batch operation
          setSelectedBatchModelIds([]); // Clear selected models
        }}
        essaysToMark={essays.filter(essay => selectedEssayIds.has(essay.id))}
        onRemarkEssay={onRemarkEssay}
        selectedModelIds={selectedBatchModelIds}
        showModelNames={isAdmin}
      />
    </div>
  );
};

export default AssessmentSelection;
